<template>
  <div class="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-3xl py-4 px-3">
    <div class="w-full max-w-5xl mx-auto space-y-4" dir="rtl">

      <!-- Header -->
      <div class="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 px-4 py-3">
        <h1 class="text-xl font-bold text-slate-50 drop-shadow-lg flex items-center gap-2">
          <span class="text-amber-300">سفارشات</span>
        </h1>
        <p class="text-sm text-slate-300/80 mt-1 font-light">
          {{ totalCount }} سفارش
        </p>
      </div>

      <!-- Filters -->
      <div class="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 p-4 space-y-3">
        <div class="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div class="col-span-1">
            <label class="block text-[10px] text-slate-400 mb-1">شماره سفارش</label>
            <input v-model="filters.id" @input="applyFilters" type="number"
                   placeholder="شماره سفارش"
                   class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60">
          </div>

          <div class="col-span-1">
            <label class="block text-[10px] text-slate-400 mb-1">نام و نام‌خانوادگی</label>
            <input v-model="filters.customerName" @input="applyFilters" type="text"
                   placeholder="نام مشتری"
                   class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60">
          </div>

          <div class="col-span-1">
            <label class="block text-[10px] text-slate-400 mb-1">شماره تلفن</label>
            <input v-model="filters.phone" @input="applyFilters" type="text"
                   placeholder="شماره موبایل"
                   class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400/60">
          </div>

          <div class="col-span-1">
            <label class="block text-[10px] text-slate-400 mb-1">وضعیت سفارش</label>
            <select v-model="filters.status" @change="applyFilters"
                    class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400/60">
              <option value="">همه وضعیت‌ها</option>
              <option v-for="(label, key) in statusLabels" :key="key" :value="key">{{ label }}</option>
            </select>
          </div>

          <div class="col-span-1">
            <label class="block text-[10px] text-slate-400 mb-1">از تاریخ</label>
            <date-field class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-100"
                        placeholder="از تاریخ"
                        v-model="filters.date_from" @input="applyFilters"></date-field>
          </div>

          <div class="col-span-1">
            <label class="block text-[10px] text-slate-400 mb-1">تا تاریخ</label>
            <date-field class="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-100"
                        placeholder="تا تاریخ"
                        v-model="filters.date_to" @input="applyFilters"></date-field>
          </div>
        </div>

        <button
            @click="resetFilters"
            class="text-xs text-slate-300 border border-white/10 rounded-2xl px-3 py-2 hover:bg-white/10 transition"
        >
          پاک کردن فیلترها
        </button>
      </div>

      <!-- Table -->
      <div class="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full table-auto text-right">
            <thead>
            <tr class="text-[11px] text-slate-400 bg-black/20">
              <th class="th">شماره</th>
              <th class="th">تاریخ</th>
              <th class="th">کد رهگیری</th>
              <th class="th">وضعیت</th>
              <th class="th">اقلام</th>
              <th class="th">مبلغ کل</th>
              <th class="th">مشتری</th>
              <th class="th">تلفن</th>
            </tr>
            </thead>
            <tbody class="text-slate-200 text-sm">
            <tr v-for="order in orders" :key="order.id"
                class="cursor-pointer border-t border-white/10 hover:bg-white/5 transition"
                @click="$router.push(`/panel/adminOrderDetail/${order.id}`)">
              <td class="td text-center font-semibold text-emerald-200">{{ order.id }}</td>
              <td class="td text-[12px] text-slate-300">{{ formatDate(order.date_time) }}</td>
              <td class="td text-[12px]">{{ order.exuni_tracking_code || '-' }}</td>
              <td class="td">
                <span :class="statusClass(order.status)">
                  {{ statusLabels[order.status] || order.status }}
                </span>
              </td>
              <td class="td text-center">
                {{ order.total_product_quantity | toMoney }}
                <span class="unit">عدد</span>
              </td>
              <td class="td text-center">
                {{ order.final_amount | toMoney }}
                <span class="unit">تومان</span>
              </td>
              <td class="td text-[12px]">
                {{ order.shipment_address && order.shipment_address.first_name }}
                {{ order.shipment_address && order.shipment_address.last_name }}
              </td>
              <td class="td text-[12px]">{{ order.customer && order.customer.mobile_number }}</td>
            </tr>

            <tr v-if="!loading && orders.length === 0">
              <td class="td text-center text-slate-500" colspan="8">سفارشی یافت نشد</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between">
        <span class="text-xs text-slate-400">صفحه {{ currentPage }} از {{ totalPages }}</span>
        <div class="flex gap-2">
          <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="text-xs rounded-2xl px-3 py-2 bg-white/5 text-slate-200 border border-white/10 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            صفحه قبل
          </button>
          <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage >= totalPages"
              class="text-xs rounded-2xl px-3 py-2 bg-white/5 text-slate-200 border border-white/10 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            صفحه بعد
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-center text-slate-400 text-sm py-4">در حال بارگذاری...</div>
    </div>
  </div>
</template>

<script>
import DateField from "@/components/mcomponents/cleave/DateField";

export default {
  name: "SupportOrdersList",
  components: {DateField},
  data() {
    return {
      orders: [],
      loading: false,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      filterDebounce: null,
      filters: {
        id: "",
        customerName: "",
        phone: "",
        status: "",
        date_from: "",
        date_to: "",
      },
      statusLabels: {
        pe: "در انتظار پرداخت",
        pa: "جاری",
        pr: "درحال بسته بندی",
        pc: "بسته بندی شد",
        sh: "ارسال شده",
        de: "تحویل شده",
        ca: "لغو شده",
        ex: "منقضی شده",
        re: "مرجوعی",
        ed: "ویرایش شده",
      },
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
  },
  methods: {
    statusClass(status) {
      const map = {
        pe: "text-amber-200 bg-amber-500/10 border border-amber-400/30 px-2 py-1 rounded-full text-[10px]",
        pa: "text-sky-200 bg-sky-500/10 border border-sky-400/30 px-2 py-1 rounded-full text-[10px]",
        pr: "text-indigo-200 bg-indigo-500/10 border border-indigo-400/30 px-2 py-1 rounded-full text-[10px]",
        pc: "text-teal-200 bg-teal-500/10 border border-teal-400/30 px-2 py-1 rounded-full text-[10px]",
        sh: "text-violet-200 bg-violet-500/10 border border-violet-400/30 px-2 py-1 rounded-full text-[10px]",
        de: "text-emerald-200 bg-emerald-500/10 border border-emerald-400/30 px-2 py-1 rounded-full text-[10px]",
        ca: "text-rose-200 bg-rose-500/10 border border-rose-400/30 px-2 py-1 rounded-full text-[10px]",
        ex: "text-slate-300 bg-slate-500/10 border border-slate-400/30 px-2 py-1 rounded-full text-[10px]",
        re: "text-slate-300 bg-slate-500/10 border border-slate-400/30 px-2 py-1 rounded-full text-[10px]",
      };
      return map[status] || "text-slate-300 bg-slate-500/10 border border-slate-400/30 px-2 py-1 rounded-full text-[10px]";
    },
    formatDate(date) {
      return date ? date.split("T")[0] : "-";
    },
    applyFilters() {
      clearTimeout(this.filterDebounce);
      this.filterDebounce = setTimeout(() => {
        this.fetchOrders(1);
      }, 400);
    },
    resetFilters() {
      this.filters = {
        id: "",
        customerName: "",
        phone: "",
        status: "",
        date_from: "",
        date_to: "",
      };
      this.fetchOrders(1);
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.fetchOrders(page);
    },
    fetchOrders(page = 1) {
      this.loading = true;
      const params = {
        limit: this.pageSize,
        offset: (page - 1) * this.pageSize,
      };
      if (this.filters.id) params.id = this.filters.id;
      if (this.filters.customerName) params.customer_name = this.filters.customerName;
      if (this.filters.phone) params.customer_phone = this.filters.phone;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.date_from) params.date_from = this.filters.date_from;
      if (this.filters.date_to) params.date_to = this.filters.date_to;

      this.request({
        url: this.endpoint("shop/supportOrderList/all"),
        params: params,
        method: "get",
        success: (data) => {
          this.loading = false;
          this.orders = data.results || [];
          this.totalCount = data.count || 0;
          this.currentPage = page;
        },
      });
    },
  },
  mounted() {
    this.fetchOrders(1);
  },
};
</script>

<style scoped>
.th {
  padding: 10px 12px;
  white-space: nowrap;
}
.td {
  padding: 10px 12px;
  white-space: nowrap;
}
.unit {
  font-size: 10px;
  color: rgba(148, 163, 184, 0.9);
  margin-right: 4px;
}
</style>
